import type { FC, ReactNode } from 'react'

import {
    ArrowRight,
    CalendarDays,
    Clock,
    MapPin,
    Sparkles
} from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import Confetti, { HERO_CONFETTI } from '@/components/landing/confetti'
import InvitationTicket from '@/components/landing/invitation-ticket'
import { invitation } from '@/constants/invitation'

const HeroSection: FC = (): ReactNode => {
    const { honoree, age, intro, date, time, venue, rsvpDeadline } = invitation

    const highlights = [
        { icon: CalendarDays, label: date.full },
        { icon: Clock, label: `Dès ${time.start}` },
        { icon: MapPin, label: venue.area }
    ]

    return (
        <section id='accueil' aria-labelledby='accueil-titre' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-32 -left-40 -z-10 size-[34rem] rounded-full bg-festive-blush/60 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute top-1/3 -right-48 -z-10 size-[30rem] rounded-full bg-festive-saffron/30 blur-3xl' />
            <Confetti pieces={HERO_CONFETTI} className='-z-10' />

            <div className='mx-auto grid max-w-6xl items-center gap-20 px-5 pt-32 pb-24 sm:px-8 sm:pt-40 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12 lg:pb-32'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none'>
                    <p className='inline-flex items-center gap-2 rounded-full bg-card/80 px-4 py-2 text-sm font-semibold text-primary shadow-sm ring-1 ring-primary/15'>
                        <Sparkles className='size-4' aria-hidden='true' />
                        Vous êtes invité·e !
                    </p>

                    <h1 id='accueil-titre' className='mt-7 font-heading text-[clamp(3rem,8vw,6rem)] leading-[0.95] tracking-tight text-balance'>
                        <span className='block'>
                            {honoree} fête
                        </span>

                        <span className='block'>
                            ses{' '}
                            <span className='relative inline-block text-primary italic'>
                                {age} ans
                                <svg
                                    aria-hidden='true'
                                    viewBox='0 0 220 18'
                                    preserveAspectRatio='none'
                                    className='absolute -bottom-2 left-0 h-3 w-full text-festive-saffron sm:-bottom-3 sm:h-4'
                                >
                                    <path d='M4 12c32-9 62-9 92-3s70 7 120-6' fill='none' stroke='currentColor' strokeWidth='6' strokeLinecap='round' />
                                </svg>
                            </span>
                        </span>
                    </h1>

                    <p className='mt-9 max-w-xl text-lg leading-relaxed text-muted-foreground text-pretty sm:text-xl'>
                        {intro}
                    </p>

                    <ul aria-label='La soirée en bref' className='mt-8 flex flex-wrap gap-2.5'>
                        {highlights.map(({ icon: Icon, label }) => (
                            <li
                                key={label}
                                className='inline-flex items-center gap-2 rounded-full bg-card px-4 py-2 text-sm font-semibold shadow-sm ring-1 ring-foreground/10'
                            >
                                <Icon className='size-4 text-primary' aria-hidden='true' />
                                {label}
                            </li>
                        ))}
                    </ul>

                    <div className='mt-10 flex flex-col gap-3 sm:flex-row sm:items-center'>
                        <a href='/#rsvp' className={buttonStyles({ size: 'lg' })}>
                            Je réponds à l’invitation
                            <ArrowRight aria-hidden='true' />
                        </a>

                        <a href='/#programme' className={buttonStyles({ variant: 'outline', size: 'lg' })}>
                            Voir le programme
                        </a>
                    </div>

                    <p className='mt-5 text-sm text-muted-foreground'>
                        Réponse souhaitée avant le {rsvpDeadline}.
                    </p>
                </div>

                <InvitationTicket />
            </div>
        </section>
    )
}

export default HeroSection
